import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/format";
import { Skeleton } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, KeyRound, Pencil, Trash2, Lock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SOURCE_BADGE = {
  database: { label: "Stored (encrypted)", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400" },
  environment: { label: "Server .env", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400" },
  not_set: { label: "Not set", cls: "bg-muted text-muted-foreground" },
};

export default function ApiKeys() {
  const [keys, setKeys] = useState(null);
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/keys").then(({ data }) => setKeys(data.keys))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/keys", { service: editing.service, value });
      toast.success(`${editing.label} saved — encrypted at rest`);
      setEditing(null); setValue("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    await api.delete(`/keys/${toDelete.service}`);
    toast.success(`${toDelete.label} removed`);
    setToDelete(null);
    load();
  };

  if (!keys) return <div className="space-y-3 max-w-3xl">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;

  const groups = [...new Set(keys.map((k) => k.group))];

  return (
    <div className="max-w-3xl space-y-8" data-testid="api-keys-page">
      <div className="bg-muted/60 border border-border rounded-lg p-5 flex items-start gap-4">
        <ShieldCheck className="h-5 w-5 text-green-600 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="text-sm text-muted-foreground space-y-1">
          <p className="font-semibold text-foreground">How your keys are protected</p>
          <p>• Keys are <strong>AES-encrypted at rest</strong> (Fernet) and never displayed or returned in full — only masked previews.</p>
          <p>• Only <strong>admin</strong> accounts can view or manage this page.</p>
          <p>• All current channel operations are <strong>read-only</strong>. Any future write action (e.g. price updates) will always show a preview and require your explicit approval first.</p>
        </div>
      </div>

      {groups.map((g) => (
        <section key={g} className="space-y-2">
          <h3 className="font-heading text-sm font-semibold tracking-tight uppercase text-muted-foreground tracking-[0.15em]">{g}</h3>
          <div className="bg-card border border-border rounded-lg divide-y divide-border">
            {keys.filter((k) => k.group === g).map((k) => {
              const s = SOURCE_BADGE[k.source];
              return (
                <div key={k.service} className="flex items-center gap-4 px-4 py-3.5" data-testid={`key-row-${k.service}`}>
                  <KeyRound className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{k.label}</p>
                    <p className="text-xs font-mono text-muted-foreground">{k.masked || "—"}</p>
                  </div>
                  <Badge className={`${s.cls} border-0 font-semibold text-[10px]`}>{s.label}</Badge>
                  <Button variant="outline" size="sm" className="h-8" onClick={() => { setEditing(k); setValue(""); }} data-testid={`key-edit-${k.service}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" aria-hidden="true" /> {k.source === "not_set" ? "Add" : "Update"}
                  </Button>
                  {k.source === "database" && (
                    <Button variant="ghost" size="sm" className="h-8 text-red-600 hover:text-red-700" onClick={() => setToDelete(k)} data-testid={`key-delete-${k.service}`}>
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4" aria-hidden="true" /> {editing?.label}</DialogTitle>
            <DialogDescription>
              The new value is encrypted before storage and takes effect immediately for live API calls. It will never be shown in full again.
            </DialogDescription>
          </DialogHeader>
          <Input type="password" placeholder="Paste new key value…" value={value} onChange={(e) => setValue(e.target.value)} data-testid="key-value-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="key-save-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving || !value.trim()} data-testid="key-save-confirm">
              {saving ? "Saving…" : "Save securely"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              The stored encrypted value will be deleted. If a value exists in the server .env it will be used as fallback; otherwise this integration stops working.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="key-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="key-delete-confirm">Remove key</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
